import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CustomModal from "../../../packages/CustomModal";
import { useToastNotification } from "../../../packages/WPToastNotification";

import { AssignLicenseFields } from "../AssignLicenseFields";
import LicensePermissionsAccordion from "../LicensePermissionsAccordion";
import { createAssignLicensePayload } from "./AssignLicenseUtils";
import SelectUsersEmail from "./SelectUsersEmail";

import { CustomModalButtons } from "../../../constants/global.constants";

import { getLicenseStatusByExpiry } from "../../../utils/LicenseUtils";

import { assignUsersLicense } from "../../../network/LicenseApis";

import "./AssignLicense.scss";

const AssignLicense = ({
  userData,
  onCloseModal,
  onLicenseAssigned,
  onUserLicenseUpdate,
}) => {
  const { t } = useTranslation();
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  const licensePermissions = useSelector((state) => state.licensePermissions);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [licenseForm, setLicenseForm] = useState({
    selectedLicenseType: null,
    selectedLicenseName: null,
    expiryDate: "",
    keyAccountManager: "",
    emailInputError: "",
    isDataChanged: false,
    clearUserError: false,
  });

  const {
    selectedLicenseName,
    expiryDate,
    emailInputError,
    isDataChanged,
    keyAccountManager,
    clearUserError,
  } = licenseForm;

  const { name = "", email = "", userId = "" } = userData || {};
  const isSingleUserMode = !!userData;

  const selectedLicenseObj = useMemo(() => {
    if (!selectedLicenseName) {
      return null;
    }
    return (licensePermissions || []).find(
      (license) => license.id === selectedLicenseName.value,
    );
  }, [licensePermissions, selectedLicenseName]);

  const handleAssignLicense = async () => {
    if (
      !selectedLicenseName ||
      !expiryDate ||
      (!isSingleUserMode && selectedUsers.length === 0)
    ) {
      return;
    }

    const licenseObj = (licensePermissions || []).find(
      (license) => license.id === selectedLicenseName.value,
    );

    if (!licenseObj) {
      showErrorNotification(t("licenseManagement.licenseAssignmentFailed"));
      return;
    }

    const payload = createAssignLicensePayload(
      isSingleUserMode ? [{ userData: [{ userId }], email }] : selectedUsers,
      licenseObj,
      expiryDate,
      keyAccountManager,
    );

    setLoadingMsg(t("licenseManagement.assigningLicense"));

    try {
      const res = await assignUsersLicense(payload);
      if (res?.status !== 200) {
        throw new Error();
      }
      showSuccessNotification(
        t("licenseManagement.licenseSuccessfullyAssigned"),
      );

      payload.forEach((userPayload, idx) => {
        const id = isSingleUserMode ? userId : userPayload?.userId;
        const newLicense = userPayload?.licenses?.[0];

        if (newLicense && id) {
          const status = getLicenseStatusByExpiry(newLicense?.expiryDate);
          const licenseWithStatus = { ...newLicense, licenseStatus: status };

          if (isSingleUserMode && idx === 0) {
            onLicenseAssigned?.(licenseWithStatus);
          }

          onUserLicenseUpdate?.(id, licenseWithStatus);
        }
      });
      onCloseModal?.();
    } catch (err) {
      showErrorNotification(t("licenseManagement.licenseAssignmentFailed"));
    } finally {
      setLoadingMsg("");
    }
  };

  const handleConfirmBtnClick = ({ target }) => {
    if (target?.id === CustomModalButtons.CONFIRM) {
      handleAssignLicense();
      return;
    }
    onCloseModal?.();
  };

  const isUserAlreadyHasLicense = selectedUsers.some(
    (user) => user.error === t("licenseManagement.userAlreadyHasLicense"),
  );

  const isAssignLicenseBtnDisabled =
    !selectedLicenseName ||
    !expiryDate ||
    (!isSingleUserMode && !!emailInputError) ||
    (!isSingleUserMode && selectedUsers.length === 0) ||
    !isDataChanged ||
    isUserAlreadyHasLicense;

  const handleEmailErrorChange = (error) => {
    setLicenseForm((prev) => ({ ...prev, emailInputError: error }));
  };

  const assignLicenseModalHeader = useMemo(
    () =>
      isSingleUserMode
        ? t("licenseManagement.assignedLicenseTo", { name })
        : t("licenseManagement.assignLicense"),
    [isSingleUserMode, t, name],
  );

  return (
    <CustomModal
      customclass="assign-license-modal"
      header={assignLicenseModalHeader}
      headerHelper={
        isSingleUserMode
          ? email
          : t("licenseManagement.assignLicenseDescription")
      }
      confirmBtn={t("licenseManagement.assignLicense")}
      cancelBtn={t("ValueStrings.close")}
      onModalClose={handleConfirmBtnClick}
      width="684px"
      confirmBtnDisabled={isAssignLicenseBtnDisabled}
      isLoading={Boolean(loadingMsg)}
      loadingText={loadingMsg}
      headerDataTestId="assign_license"
      profileInitials={name?.charAt(0)?.toUpperCase()}
    >
      <div className="assign-license-content">
        <AssignLicenseFields
          licenseForm={licenseForm}
          setLicenseForm={setLicenseForm}
          userData={userData}
          isAssignLicense
          dataTestId="start_license_date"
        />

        {!isSingleUserMode && (
          <>
            <hr className="modal-section-divider" />
            <div className="select-users-table">
              <SelectUsersEmail
                selectedLicenseName={selectedLicenseName}
                onUsersChange={setSelectedUsers}
                onInputErrorChange={handleEmailErrorChange}
                clearUserError={clearUserError}
              />
            </div>
          </>
        )}

        {selectedLicenseObj && (
          <>
            <hr className="modal-section-divider" />
            <LicensePermissionsAccordion licenseObj={selectedLicenseObj} />
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default AssignLicense;
